import { useState, useRef, useEffect, useCallback, useMemo, MutableRefObject, memo } from "react";
import { useDispatch } from "react-redux";
import { Virtuoso } from "react-virtuoso";
import { m, LazyMotion, domAnimation } from "framer-motion";
import classNames from "classnames";

import { AnswerMap } from "../../../../../../../../store/reducers/admin/surveys";
import { useTypedSelector } from "../../../../../../../../store/reducers/use-typed-selector";
import { RegFieldsEnum, TextAnswer } from "../../../../../../../../types/working-model";
import { OptionalComponent } from "../../../../../../../general-ui/animated/optional-component";
import WaitingIndicator from "../../../../../../../general-ui/waiting-indicator/waiting-indicator";
import textAnswerLoader from "./text-answer-loader";

const TEXT_SURVEY_ANSWER_BATCH_SIZE = 50;

type QuestionCardTextRepliesProps = {
	language: string;
	questionId: number;
	sessionUuid: string;
}

type QuestionCardTextProps = {
	reply: TextAnswer;
	language: string;
}
const QuestionCardTextReply = memo((props: QuestionCardTextProps) => {
	const { reply, language } = props;
	const firstName = reply.profile[RegFieldsEnum.first_name] ?? "Anonymous";
	const lastName = reply.profile[RegFieldsEnum.last_name] ?? "";
	const timer = useRef<NodeJS.Timeout | null>(null);
	const [showPulse, setShowPulse] = useState(false);

	const isRetake = reply.isRetake;

	useEffect(() => {
		if (isRetake) {
			setShowPulse(true);
			if (timer.current) clearTimeout(timer.current);
			timer.current = setTimeout(() => {
				setShowPulse(false);
			}, 3000);
		} else {
			setShowPulse(false);
		}

		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [isRetake]);

	// If incoming reply is a retake, let's a add a one time pulse box-shadow animation to the new/updated reply to draw attention to it
	return (
		<div className={"survey-results-question-card-reply"}>
			<div
				className={
					classNames(
						"survey-results-question-card-reply-text",
						{
							"survey-retake-pulse": showPulse,
						}
					)
				}
			>
				<label>{firstName} {lastName}</label>
				<p>{reply.text_answer[language] ?? reply.text_answer.base}</p>
			</div>
		</div>
	);
});

const QuestionCardTextReplies = ({ questionId, language, sessionUuid }: QuestionCardTextRepliesProps) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const textAnswers = useTypedSelector(state => state.SurveysReducer.textResults);
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const answerMap = textAnswers[questionId];
	const total = answerMap?.total ?? 0;
	const answersRef = useRef<AnswerMap>(answerMap);
	const sessionUuidRef = useRef<string>(sessionUuid);
	const tokenRef = useRef<string | null>(token);
	const questionIdRef = useRef<number>(questionId);
	const channelRef = useRef<number>(user?.active_channel ?? 0);

	useEffect(() => {
		answersRef.current = answerMap;
		sessionUuidRef.current = sessionUuid;
		tokenRef.current = token;
		questionIdRef.current = questionId;
		channelRef.current = user?.active_channel ?? 0;
	}, [token, sessionUuid, questionId, user?.active_channel, answerMap]);

	useEffect(() => {
		if (!token || !user || answersRef.current?.answers?.size) return;

		setLoading(true);

		textAnswerLoader.load({
			token,
			sessionUuid,
			questionId,
			dispatch,
			channel: user.active_channel,
			limit: TEXT_SURVEY_ANSWER_BATCH_SIZE,
		}).finally(() => {
			setLoading(false);
		});
	}, [token, user, sessionUuid, questionId, dispatch]);

	const rowRenderer = useCallback((_index: number, answer: TextAnswer) => {
		return (
			<div style={{ paddingBottom: 8 }}>
				<QuestionCardTextReply reply={answer} language={language} />
			</div>
		);
	}, [language]);

	// Keep a stable renderer reference so re-renders from parent don't force
	// Virtuoso to remount every row.
	const rowRendererRef = useRef(rowRenderer) as MutableRefObject<typeof rowRenderer>;
	rowRendererRef.current = rowRenderer;
	const stableRowRenderer = useCallback((idx: number, answer: TextAnswer) => rowRendererRef.current(idx, answer), []);

	const items = useMemo(() => {
		return Array.from(answerMap?.answers?.values() || []);
	}, [answerMap]);

	return (
		<div className="survey-results-question-card-replies">
			{((loading && !answerMap?.answers?.size)) ? (
				<div style={{ height: 56 }
				}>
					<WaitingIndicator fillSpace transparentFill />
				</div>
			) : (
				<>
					<LazyMotion features={domAnimation}>
						<m.div
							initial={{ opacity: 0, x: -50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ type: 'spring', stiffness: 260, damping: 30 }}
						>
							{!answerMap?.answers?.size ? (
								<label className="survey-results-question-card-empty">No responses yet</label>
							) : (
								<>
									<Virtuoso
										data={items}
										style={{ height: 'calc(100vh - 284px)' }}
										computeItemKey={(_, answer) => answer.uuid || answer.id}
										/*
											items within 200px of the viewport stay mounted, reducing how frequently cards unmount for normal scrolling.
											This helps with maintaining card state (within a reasonable scroll).
										*/
										overscan={200}
										followOutput={false}
										atBottomThreshold={100}
										async
										atBottomStateChange={atBottom => {
											if (loading) return;
											if (!atBottom) return;
											if (!answerMap?.answers?.size) return;
											if (answerMap?.answers?.size >= total) return;

											// fetch next batch
											const offset = answerMap?.answers?.size || 0;
											textAnswerLoader.next({
												questionId,
												limit: TEXT_SURVEY_ANSWER_BATCH_SIZE,
												offset,
											});
										}}
										itemContent={stableRowRenderer}
									/>
								</>
							)}
						</m.div>
					</LazyMotion>

					<OptionalComponent display={loading && answerMap.answers.size < total} usingHeight={50} >
						<div key="loading-more" style={{ height: 56 }}>
							<WaitingIndicator fillSpace transparentFill />
						</div>
					</OptionalComponent>
				</>
			)
			}
		</div >
	);
};

export default QuestionCardTextReplies;